import { Link } from '@tanstack/react-router'
import { COLORS, FONTS, NEUTRAL9_RGB } from './home/tokens'
import { useConsent } from '@/providers/consent-provider'
import { useIsMobile } from '@/lib/hooks'

type FooterLink = { label: string; to?: string; href?: string; onClick?: () => void }
type Column = { heading: string; links: FooterLink[] }

// Column order follows the Figma footer (Legal, Company, Social); Resources
// was added after Company so /blog, /news, and /press have always-rendered
// links on every page — the header only exposes them in a dropdown, and pages
// without crawlable inbound links don't get indexed.
const COLUMNS: Column[] = [
  {
    heading: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy/' },
      { label: 'Terms of Use', to: '/terms/' },
      { label: 'Subscription Terms', to: '/subscription/' },
      { label: 'Sub-Processors', to: '/sub-processors/' },
      { label: 'Data Processing Addendum', to: '/dpa/' },
    ],
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', to: '/company/' },
      { label: 'Careers', to: '/joinus/' },
    ],
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Blog', to: '/blog/' },
      { label: 'News', to: '/news/' },
      { label: 'Press Release', to: '/press/' },
    ],
  },
  {
    heading: 'Social',
    links: [
      { label: 'LinkedIn', href: 'https://www.linkedin.com/company/ent-security' },
      { label: 'X', href: 'https://x.com/Ent_Security' },
    ],
  },
]

/**
 * Global site footer (Legal / Company). Rebuilt from the Figma
 * design (node 976:154).
 *
 * It does NOT own a cymatic and has no background of its own — it's always
 * transparent and sits directly on the page background. On the homepage the
 * single full-page cymatic backdrop shows through (the backdrop transitions
 * into the rune as this footer scrolls into view, keyed off
 * `data-section="footer"`). The gradient overlay fades whatever is behind into
 * ink toward the bottom and sits on top of it.
 */
export function SiteFooter() {
  const { openPreferences } = useConsent()
  const isMobile =
    useIsMobile() || (typeof window !== 'undefined' && window.innerWidth < 768)

  // Legal column gains a permanent "Cookie Preferences" entry (GDPR: consent
  // must be changeable at any time). Injected here so it can call the hook.
  const columns: Column[] = COLUMNS.map((col) =>
    col.heading === 'Legal'
      ? { ...col, links: [...col.links, { label: 'Cookie Preferences', onClick: openPreferences }] }
      : col,
  )
  const year = new Date().getFullYear()

  const linkBase: React.CSSProperties = {
    fontFamily: FONTS.body,
    fontSize: 'var(--text-sm)',
    fontWeight: 400,
    lineHeight: 2,
    letterSpacing: '-0.128px',
    background: 'none',
    border: 'none',
    padding: 0,
    textAlign: 'left',
    display: 'block',
    textDecoration: 'none',
    // On touch viewports give each link a ≥44px tap target (vertically centered)
    // so adjacent footer links aren't too small/close to tap. Desktop keeps the
    // tighter line-height-2 rhythm.
    ...(isMobile
      ? { display: 'flex', alignItems: 'center', minHeight: 44, lineHeight: 1.3 }
      : {}),
  }

  return (
    <footer
      data-section="footer"
      style={{
        position: 'relative',
        zIndex: 10,
        minHeight: 760,
        // Always transparent: the footer sits directly on the page background.
        backgroundColor: 'transparent',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .ent-footer-link { color: ${COLORS.neutral1}; cursor: pointer; transition: color 0.2s ease; }
        .ent-footer-link:hover { color: ${COLORS.green1}; }
      `}</style>

      {/* Gradient overlay — fades the pattern behind into ink toward the
          bottom. The transparent stop is anchored at width·sin(8.56°) ≈
          14.85vw along the tilted axis so the fade's meeting line lands
          exactly on the top-LEFT corner (the fixed 19.9% stop left that
          corner tinted on wide screens — a visible hard edge). */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            `linear-gradient(188.56deg, rgba(${NEUTRAL9_RGB},0) 14.85vw, rgba(${NEUTRAL9_RGB},1) calc(14.85vw + 320px))`,
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1728,
          margin: '0 auto',
          minHeight: 760,
          padding: '96px clamp(24px, 9.26vw, 160px) 48px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 80,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 64, marginTop: 100 }}>
          {columns.map((col) => {
            const headingId = `footer-col-${col.heading.toLowerCase().replace(/\s+/g, '-')}`
            return (
              <div key={col.heading} style={{ minWidth: 150 }}>
                <h3
                  id={headingId}
                  style={{
                    fontFamily: FONTS.heading,
                    fontWeight: 200,
                    fontSize: 'var(--text-xl)',
                    letterSpacing: '-0.192px',
                    color: COLORS.neutral5,
                    margin: 0,
                  }}
                >
                  {col.heading}
                </h3>
                {/* Short accent line beneath the header */}
                <div style={{ width: 24.5, height: 1, backgroundColor: COLORS.neutral5, opacity: 0.6, margin: '18px 0 14px' }} />
                <nav aria-labelledby={headingId}>
                  {col.links.map((link) =>
                    link.to ? (
                      // Real anchor (crawlable) with client-side navigation
                      <Link key={link.label} to={link.to} className="ent-footer-link" style={linkBase}>
                        {link.label}
                      </Link>
                    ) : link.href ? (
                      // External link (social) — new tab
                      <a key={link.label} href={link.href} target="_blank" rel="noopener noreferrer" className="ent-footer-link" style={linkBase}>
                        {link.label}
                      </a>
                    ) : link.onClick ? (
                      // Action link (e.g. open cookie preferences)
                      <button
                        key={link.label}
                        type="button"
                        onClick={link.onClick}
                        className="ent-footer-link"
                        style={{ ...linkBase, background: 'none', border: 'none', cursor: 'pointer' }}
                      >
                        {link.label}
                      </button>
                    ) : (
                      <span key={link.label} style={{ ...linkBase, color: COLORS.neutral5, opacity: 0.7 }}>
                        {link.label}
                      </span>
                    ),
                  )}
                </nav>
              </div>
            )
          })}
        </div>

        <span
          style={{
            fontFamily: FONTS.heading,
            fontWeight: 200,
            fontSize: 'var(--text-xl)',
            letterSpacing: '-0.192px',
            color: COLORS.neutral5,
          }}
        >
          ©{year}
        </span>
      </div>
    </footer>
  )
}

export default SiteFooter
